import React from 'react';
import { Link } from 'react-router-dom';
import { Scissors, Briefcase, TrendingUp, ArrowRight, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { track2Data } from '../mock';

const Track2 = () => {
  const iconMap = {
    'scissors': Scissors,
    'briefcase': Briefcase,
    'trending-up': TrendingUp
  };

  return (
    <div className="bg-white min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-gradient-to-br from-[#D80000] to-[#b00000] text-white">
        <div className="absolute top-8 right-8 bg-white text-[#D80000] px-6 py-3 rounded-full text-sm font-bold uppercase shadow-lg z-20">
          Launching 2026
        </div>
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="max-w-4xl">
            <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-bold uppercase mb-6">
              {track2Data.subtitle}
            </div>
            <h1 className="font-black text-5xl md:text-8xl leading-[0.9] uppercase mb-6">
              Track 2
            </h1>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Mastery Through Trade
            </h2>
            <p className="text-white/90 text-sm md:text-base uppercase tracking-wider mb-8">
              {track2Data.tagline}
            </p>
            <p className="text-white/95 text-lg md:text-xl leading-relaxed mb-10">
              {track2Data.description}
            </p>
            <Link to="/contact">
              <Button className="bg-white text-[#D80000] hover:bg-gray-100 rounded-full px-8 py-6 text-base font-semibold uppercase transition-all hover:scale-105 shadow-lg">
                Express Interest
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="py-20">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="text-center mb-16">
            <h2 className="font-black text-4xl md:text-6xl text-[#0A277A] uppercase mb-4">
              Program Components
            </h2>
            <p className="text-gray-600 text-lg">
              Building skills, income, and self-sufficiency through the barbering trade
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {track2Data.programs.map((program) => {
              const Icon = iconMap[program.icon];
              return (
                <Card key={program.id} className="bg-gray-50 border-2 border-gray-200 p-8 hover:border-[#D80000] hover:shadow-lg transition-all duration-300">
                  <div className="bg-[#D80000] text-white w-16 h-16 rounded-full flex items-center justify-center mb-6">
                    <Icon size={32} />
                  </div>
                  <h3 className="font-black text-2xl text-[#0A277A] uppercase mb-4">
                    {program.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    {program.description}
                  </p>
                  {program.launchDate && (
                    <div className="inline-block bg-[#D80000]/10 text-[#D80000] px-3 py-1 rounded-full text-xs font-bold uppercase">
                      Launching {program.launchDate}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="text-center mb-12">
            <h2 className="font-black text-4xl md:text-6xl text-[#0A277A] uppercase mb-4">
              Mastery Through Trade
            </h2>
            <p className="text-gray-600 text-lg">
              Professional training environment where veterans transform discipline into art
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {track2Data.images.map((image, idx) => (
              <div key={idx} className="aspect-square overflow-hidden rounded-lg shadow-lg group">
                <img
                  src={image}
                  alt={`Barbershop ${idx + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Scholarships & Support */}
      <section className="py-20">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="bg-[#0A277A] text-white p-12 md:p-16 rounded-lg">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-black text-3xl md:text-5xl uppercase mb-6">
                Scholarships Available
              </h2>
              <p className="text-white/90 text-lg leading-relaxed mb-8">
                Discounted tuition for veterans and dependents. Scholarships funded by nonprofit donors and sponsors. We believe financial barriers should never prevent someone from pursuing their purpose.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/donate">
                  <Button className="bg-[#D80000] text-white hover:bg-[#b00000] rounded-full px-8 py-4 font-semibold uppercase transition-all">
                    Sponsor a Student
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button className="bg-white text-[#0A277A] hover:bg-gray-100 rounded-full px-8 py-4 font-semibold uppercase transition-all">
                    Apply for Scholarship
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <div className="text-center mb-12">
            <h2 className="font-black text-4xl md:text-5xl text-[#0A277A] uppercase mb-4">
              2026 Launch Timeline
            </h2>
            <p className="text-gray-600 text-lg">
              Building the foundation for vocational excellence
            </p>
          </div>
          <div className="space-y-6">
            <Card className="bg-white border-l-4 border-[#D80000] p-6">
              <div className="flex items-start space-x-4">
                <Calendar className="text-[#D80000] flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-bold text-[#0A277A] text-lg mb-2">Q1 2026: Program Development</h3>
                  <p className="text-gray-700">Curriculum finalization and instructor recruitment</p>
                </div>
              </div>
            </Card>
            <Card className="bg-white border-l-4 border-[#3F54C8] p-6">
              <div className="flex items-start space-x-4">
                <Calendar className="text-[#3F54C8] flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-bold text-[#0A277A] text-lg mb-2">Q2 2026: Enrollment Opens</h3>
                  <p className="text-gray-700">Application process and student selection</p>
                </div>
              </div>
            </Card>
            <Card className="bg-white border-l-4 border-[#0A277A] p-6">
              <div className="flex items-start space-x-4">
                <Calendar className="text-[#0A277A] flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-bold text-[#0A277A] text-lg mb-2">Q3 2026: First Cohort Begins</h3>
                  <p className="text-gray-700">Official launch of MOS Academy Barber Training Program</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-5 md:px-10 text-center">
          <h2 className="font-black text-3xl md:text-5xl text-[#0A277A] uppercase mb-6">
            Be Part of the Launch
          </h2>
          <p className="text-gray-700 text-lg mb-8">
            Express your interest now to receive updates and early enrollment opportunities.
          </p>
          <Link to="/contact">
            <Button className="bg-[#D80000] text-white hover:bg-[#b00000] rounded-full px-10 py-6 text-lg font-semibold uppercase transition-all hover:scale-105">
              Get Early Access
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Track2;